import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Tooltip, Typography } from '@mui/material';
import { Block, CheckCircle, Edit as EditIcon, ExpandMore, Lock, Visibility } from '@mui/icons-material';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { WarehouseAPI } from '../../services/api';
import { usePermissionContext } from '../../contexts/PermissionContext';
import { Capability, FEATURES, FEATURE_KEYS } from '../../rbac/features';
import { display, eyebrow } from '../actionables/brand';
import { roleColor, roleLabel, ROLE_KEYS } from '../actionables/roles';

interface RoleRow {
  key: string;
  name: string;
  permissions: Record<string, Capability>;
  isSystem: boolean;
  locked: boolean;
}

// Build the full 13-key map for a role, defaulting any absent key to 'none' (fail-closed,
// matching the backend resolver). Admin resolves to full-edit.
const fullMap = (r: RoleRow): Record<string, Capability> => {
  const out: Record<string, Capability> = {};
  for (const k of FEATURE_KEYS) {
    out[k] = r.key === 'admin' ? 'edit' : (r.permissions?.[k] as Capability) || 'none';
  }
  return out;
};

const snapshot = (m: Record<string, Capability>) => FEATURE_KEYS.map((k) => `${k}:${m[k]}`).join('|');

const CAP_ORDER: Capability[] = ['none', 'view', 'edit'];
const CAP_LABEL: Record<Capability, string> = { none: 'None', view: 'View', edit: 'Edit' };

// Distinct, instantly-legible colours per capability so View vs Edit never look alike.
// Edit = Salty orange, View = calm teal/blue, None = muted grey.
const CAP_COLOR: Record<Capability, { main: string; bg: string; text: string }> = {
  none: { main: '#8A8078', bg: '#f2efe8', text: '#6b6258' },
  view: { main: '#0e7490', bg: '#e2f5f8', text: '#0e7490' },
  edit: { main: '#DC5829', bg: '#fcece4', text: '#b4431d' },
};

const CAP_ICON: Record<Capability, React.ReactNode> = {
  none: <Block sx={{ fontSize: 14 }} />,
  view: <Visibility sx={{ fontSize: 14 }} />,
  edit: <EditIcon sx={{ fontSize: 14 }} />,
};

// Count a role's capability spread for the at-a-glance summary chips.
const capCounts = (m: Record<string, Capability>) => {
  const c = { none: 0, view: 0, edit: 0 } as Record<Capability, number>;
  for (const k of FEATURE_KEYS) c[m[k] || 'none'] += 1;
  return c;
};

// A small coloured dot in the role's accent colour.
const RoleDot: React.FC<{ roleKey: string; size?: number }> = ({ roleKey, size = 12 }) => (
  <Box sx={{ width: size, height: size, borderRadius: '50%', bgcolor: roleColor(roleKey).main, flexShrink: 0, boxShadow: `0 0 0 3px ${roleColor(roleKey).bg}` }} />
);

// Animated segmented None/View/Edit control. A sliding/scaling highlight marks the active cell
// via framer-motion's shared layout (layoutId); reduced-motion falls back to an instant swap.
const CapabilitySegmented: React.FC<{
  value: Capability;
  groupId: string;
  disabled?: boolean;
  reduceMotion: boolean | null;
  onChange: (cap: Capability) => void;
  ariaLabel: string;
}> = ({ value, groupId, disabled, reduceMotion, onChange, ariaLabel }) => (
  <Box
    role="radiogroup"
    aria-label={ariaLabel}
    sx={{
      display: 'inline-flex', p: '3px', borderRadius: '999px', bgcolor: '#f4f1ea',
      border: '1px solid #e7e1d4', flexShrink: 0, opacity: disabled ? 0.65 : 1,
    }}
  >
    {CAP_ORDER.map((cap) => {
      const active = value === cap;
      const c = CAP_COLOR[cap];
      return (
        <Box
          key={cap}
          role="radio"
          aria-checked={active}
          aria-label={CAP_LABEL[cap]}
          onClick={() => { if (!disabled && !active) onChange(cap); }}
          sx={{
            position: 'relative', cursor: disabled ? 'default' : 'pointer', userSelect: 'none',
            px: 1.4, py: 0.4, borderRadius: '999px', minWidth: 58,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 0.5,
            fontSize: '0.78rem', fontWeight: 700,
            color: active ? '#fff' : c.text,
            transition: 'color 160ms',
          }}
        >
          {active && (
            <motion.span
              layout={!reduceMotion}
              layoutId={reduceMotion ? undefined : `${groupId}-active`}
              transition={reduceMotion ? { duration: 0 } : { type: 'spring', stiffness: 420, damping: 34 }}
              style={{
                position: 'absolute', inset: 0, borderRadius: 999, backgroundColor: c.main, zIndex: 0,
              }}
            />
          )}
          <Box component="span" sx={{ position: 'relative', zIndex: 1, display: 'inline-flex', alignItems: 'center' }}>{CAP_ICON[cap]}</Box>
          <Box component="span" sx={{ position: 'relative', zIndex: 1 }}>{CAP_LABEL[cap]}</Box>
        </Box>
      );
    })}
  </Box>
);

// A small tinted count chip for the header summary ("8 edit", "1 view", "4 no access").
const CountChip: React.FC<{ cap: Capability; count: number }> = ({ cap, count }) => {
  const c = CAP_COLOR[cap];
  const label = cap === 'none' ? 'no access' : cap;
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.25, borderRadius: '999px', bgcolor: c.bg, color: c.text, fontSize: '0.74rem', fontWeight: 700 }}>
      {CAP_ICON[cap]} {count} {label}
    </Box>
  );
};

// Roles & Permissions matrix editor. Admin is collapsed to a one-line summary (expandable, never
// editable). Each of the other 5 roles is a compact card with the 13 features in a 2-column grid,
// at-a-glance summary chips in the header and a per-role Save (dirty-state + Discard). Save calls
// PUT /api/roles/:key with the full 13-key map, then refreshes the caller's own permissions so
// the change takes effect with no redeploy.
const RolePermissionsMatrix: React.FC = () => {
  const reduceMotion = useReducedMotion();
  const { refresh } = usePermissionContext();
  const [roles, setRoles] = useState<RoleRow[]>([]);
  const [draft, setDraft] = useState<Record<string, Record<string, Capability>>>({});
  const [baseline, setBaseline] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [savedKey, setSavedKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await WarehouseAPI.getRoles();
      // Order by the canonical ROLE_KEYS display order (admin first, catalog-member right
      // after catalog-manager, etc.) rather than the API's return/insertion order — otherwise
      // a newly-inserted role (e.g. Catalog Member) lands at the bottom. Unknown keys not in
      // ROLE_KEYS sort to the end, alphabetically, so nothing is ever dropped.
      const orderOf = (key: string) => {
        const i = ROLE_KEYS.indexOf(key);
        return i === -1 ? ROLE_KEYS.length : i;
      };
      const sorted = [...data].sort((a, b) => {
        const oa = orderOf(a.key);
        const ob = orderOf(b.key);
        return oa !== ob ? oa - ob : a.key.localeCompare(b.key);
      });
      const rows = sorted as RoleRow[];
      const maps: Record<string, Record<string, Capability>> = {};
      const base: Record<string, string> = {};
      rows.forEach((r) => {
        const m = fullMap(r);
        maps[r.key] = m;
        base[r.key] = snapshot(m);
      });
      setRoles(rows);
      setDraft(maps);
      setBaseline(base);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load roles');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setCell = (roleKey: string, featureKey: string, cap: Capability) => {
    setDraft((prev) => ({ ...prev, [roleKey]: { ...prev[roleKey], [featureKey]: cap } }));
  };

  const dirtyRoles = useMemo(() => {
    const d: Record<string, boolean> = {};
    roles.forEach((r) => { d[r.key] = draft[r.key] && baseline[r.key] !== snapshot(draft[r.key]); });
    return d;
  }, [roles, draft, baseline]);

  const editableRoles = roles.filter((r) => r.key !== 'admin' && !r.locked);
  const adminRole = roles.find((r) => r.key === 'admin');

  const save = async (roleKey: string) => {
    setSavingKey(roleKey);
    setError(null);
    try {
      const updated = await WarehouseAPI.updateRolePermissions(roleKey, draft[roleKey]);
      const m = fullMap(updated as RoleRow);
      setDraft((prev) => ({ ...prev, [roleKey]: m }));
      setBaseline((prev) => ({ ...prev, [roleKey]: snapshot(m) }));
      // Reflect the change in the current session with no redeploy.
      refresh();
      setSavedKey(roleKey);
      setTimeout(() => setSavedKey((k) => (k === roleKey ? null : k)), 2200);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Could not save this role');
    } finally {
      setSavingKey(null);
    }
  };

  const discard = (roleKey: string) => {
    const r = roles.find((x) => x.key === roleKey);
    if (r) setDraft((prev) => ({ ...prev, [roleKey]: fullMap(r) }));
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;

  return (
    <Box>
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '10px' }}>{error}</Alert>}
      <Typography sx={{ fontSize: '0.88rem', color: 'text.secondary', mb: 2.5, maxWidth: 820 }}>
        Toggle each role&apos;s access per tab — None, View or Edit. Edit implies View. The Admin
        role is locked to full access and cannot be changed. Saving a role applies within about 30
        seconds for everyone, and immediately for you on your next navigation (no redeploy needed).
      </Typography>

      {adminRole && (
        <AdminCard role={adminRole} reduceMotion={reduceMotion} />
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: adminRole ? 2 : 0 }}>
        {editableRoles.map((r, i) => (
          <EditableRoleCard
            key={r.key}
            role={r}
            index={i}
            map={draft[r.key] || {}}
            dirty={!!dirtyRoles[r.key]}
            saving={savingKey === r.key}
            saved={savedKey === r.key}
            reduceMotion={reduceMotion}
            onSetCell={setCell}
            onSave={save}
            onDiscard={discard}
          />
        ))}
      </Box>
    </Box>
  );
};

// A single editable role card. Collapsed by default to a one-line summary (role dot + name +
// at-a-glance count chips + chevron + Save/Discard area); clicking the title/chevron expands the
// segmented None/View/Edit feature grid. A dirty role force-expands so its unsaved toggles and
// Save button can never be hidden. The Save/Discard buttons stop click propagation so they never
// toggle the collapse. Motion mirrors AdminCard and is instant under reduced-motion.
const EditableRoleCard: React.FC<{
  role: RoleRow;
  index: number;
  map: Record<string, Capability>;
  dirty: boolean;
  saving: boolean;
  saved: boolean;
  reduceMotion: boolean | null;
  onSetCell: (roleKey: string, featureKey: string, cap: Capability) => void;
  onSave: (roleKey: string) => void;
  onDiscard: (roleKey: string) => void;
}> = ({ role, index, map, dirty, saving, saved, reduceMotion, onSetCell, onSave, onDiscard }) => {
  const [expanded, setExpanded] = useState(false);
  const counts = capCounts(map);
  const rc = roleColor(role.key);
  // A dirty role always stays open so unsaved edits + Save remain reachable.
  const effectiveExpanded = expanded || dirty;
  return (
    <motion.div
      layout={!reduceMotion}
      initial={reduceMotion ? false : { opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28, delay: Math.min(index * 0.04, 0.2), ease: [0.16, 1, 0.3, 1] }}
    >
      <Box
        sx={{
          position: 'relative', overflow: 'hidden', borderRadius: '16px', bgcolor: '#fff',
          border: '1px solid', borderColor: dirty ? '#DC5829' : '#ececE4',
          boxShadow: '0 1px 2px rgba(26,20,15,0.04), 0 10px 28px rgba(26,20,15,0.06)',
          transition: 'border-color 160ms',
          '&::before': { content: '""', position: 'absolute', left: 0, top: 0, right: 0, height: 3, bgcolor: rc.main },
        }}
      >
        <Box
          onClick={() => setExpanded((v) => !v)}
          sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, p: 2.5, cursor: 'pointer', flexWrap: 'wrap' }}
        >
          <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <RoleDot roleKey={role.key} size={14} />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ ...eyebrow, color: '#8A8078' }}>Role</Typography>
              <Typography sx={{ ...display('1.25rem', '-0.025em'), mt: 0.2 }}>{roleLabel(role.key)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, ml: 1, flexWrap: 'wrap' }}>
              <CountChip cap="edit" count={counts.edit} />
              <CountChip cap="view" count={counts.view} />
              <CountChip cap="none" count={counts.none} />
            </Box>
          </Box>
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0, cursor: 'default' }}
          >
            <AnimatePresence>
              {saved && (
                <motion.span initial={reduceMotion ? false : { opacity: 0, x: 6 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#15803d', fontSize: '0.8rem', fontWeight: 700 }}>
                  <CheckCircle sx={{ fontSize: 16 }} /> Saved
                </motion.span>
              )}
            </AnimatePresence>
            {dirty && (
              <Button onClick={() => onDiscard(role.key)} sx={{ textTransform: 'none', fontWeight: 700, color: 'text.secondary' }}>Discard</Button>
            )}
            <Button
              variant="contained" disableElevation disabled={!dirty || saving}
              onClick={() => onSave(role.key)}
              startIcon={saving ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : undefined}
              sx={{
                textTransform: 'none', fontWeight: 800, borderRadius: '999px', px: 2.5, minHeight: 38,
                bgcolor: '#1A140F', color: '#fff', '&:hover': { bgcolor: '#000' },
                // Disabled state keeps the dark fill and white label for a consistent CTA look,
                // dimmed via opacity to read as non-interactive. White-on-#C9C2B4 was only 1.77:1
                // and failed contrast, so we dim the dark button instead of switching to a light fill.
                '&.Mui-disabled': { bgcolor: '#1A140F', color: '#fff', opacity: 0.45 },
              }}
            >
              Save changes
            </Button>
            <ExpandMore
              onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
              sx={{ color: '#8A8078', cursor: 'pointer', transition: 'transform 180ms', transform: effectiveExpanded ? 'rotate(180deg)' : 'none' }}
            />
          </Box>
        </Box>

        <AnimatePresence initial={false}>
          {effectiveExpanded && (
            <motion.div
              initial={reduceMotion ? false : { height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={reduceMotion ? { opacity: 0 } : { height: 0, opacity: 0 }}
              transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }}
              style={{ overflow: 'hidden' }}
            >
              <Box sx={{ px: 2.5, pb: 2.5, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, columnGap: 4, rowGap: 0.25 }}>
                {FEATURES.map((f) => (
                  <Box
                    key={f.key}
                    sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
                      py: 0.6, borderBottom: '1px solid #f4f1ea',
                    }}
                  >
                    <Typography sx={{ fontSize: '0.86rem', fontWeight: 600, color: '#1A140F', minWidth: 0 }}>{f.label}</Typography>
                    <CapabilitySegmented
                      value={map[f.key] || 'none'}
                      groupId={`${role.key}-${f.key}`}
                      reduceMotion={reduceMotion}
                      onChange={(cap) => onSetCell(role.key, f.key, cap)}
                      ariaLabel={`${roleLabel(role.key)} access for ${f.label}`}
                    />
                  </Box>
                ))}
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
      </Box>
    </motion.div>
  );
};

// Admin card — collapsed by default to a compact one-line summary. Expandable to peek at the full
// list, but every feature is fixed to full access and never editable.
const AdminCard: React.FC<{ role: RoleRow; reduceMotion: boolean | null }> = ({ role, reduceMotion }) => {
  const [expanded, setExpanded] = useState(false);
  const rc = roleColor('admin');
  return (
    <motion.div
      layout={!reduceMotion}
      initial={reduceMotion ? false : { opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
    >
      <Box
        sx={{
          position: 'relative', overflow: 'hidden', borderRadius: '16px', bgcolor: '#faf8f3',
          border: '1px solid #ececE4',
          boxShadow: '0 1px 2px rgba(26,20,15,0.04), 0 10px 28px rgba(26,20,15,0.06)',
          '&::before': { content: '""', position: 'absolute', left: 0, top: 0, right: 0, height: 3, bgcolor: rc.main },
        }}
      >
        <Box
          onClick={() => setExpanded((v) => !v)}
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, cursor: 'pointer' }}
        >
          <RoleDot roleKey="admin" size={14} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ ...display('1.1rem', '-0.02em'), display: 'inline' }}>{roleLabel(role.key)}</Typography>
            <Typography component="span" sx={{ fontSize: '0.85rem', color: 'text.secondary', ml: 1 }}>
              Full access to all {FEATURE_KEYS.length} features
            </Typography>
          </Box>
          <Tooltip title="Admin always has full access and cannot be edited">
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: '#8A8078', fontSize: '0.78rem', fontWeight: 700, flexShrink: 0 }}>
              <Lock sx={{ fontSize: 16 }} /> Locked
            </Box>
          </Tooltip>
          <ExpandMore sx={{ color: '#8A8078', flexShrink: 0, transition: 'transform 180ms', transform: expanded ? 'rotate(180deg)' : 'none' }} />
        </Box>
        <AnimatePresence initial={false}>
          {expanded && (
            <motion.div
              initial={reduceMotion ? false : { height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={reduceMotion ? { opacity: 0 } : { height: 0, opacity: 0 }}
              transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }}
              style={{ overflow: 'hidden' }}
            >
              <Box sx={{ px: 2, pb: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, columnGap: 4, rowGap: 0.25 }}>
                {FEATURES.map((f) => (
                  <Box key={f.key} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 0.6, borderBottom: '1px solid #efe9db' }}>
                    <Typography sx={{ fontSize: '0.86rem', fontWeight: 600, color: '#1A140F' }}>{f.label}</Typography>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.2, py: 0.4, borderRadius: '999px', bgcolor: CAP_COLOR.edit.bg, color: CAP_COLOR.edit.text, fontSize: '0.78rem', fontWeight: 700 }}>
                      <EditIcon sx={{ fontSize: 14 }} /> Edit
                    </Box>
                  </Box>
                ))}
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
      </Box>
    </motion.div>
  );
};

export default RolePermissionsMatrix;
